/**
 * TikTok 发品表单提交前本地轻量校验。
 * 对齐 Create Product / Check Product Listing（client/docs/tiktok）；
 * 正式发品以平台 listing_check 为准，本地仅挡明显缺项与文档硬规则。
 * @returns {{ ok: true } | { ok: false, message: string, section?: string, field?: string }}
 */

import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import {
  attributeDisplayLabel,
  isBlankTiktokAttributeValueId,
  resolveSkcColorAgainstTemplate,
} from '@/tiktok-platform/utils/tiktokAttributeTemplate';
import { tiktokShopCapabilities } from '@/tiktok-platform/utils/tiktokShopCapabilities';
import { validateSupplierSkusLocal } from '@/tiktok-platform/utils/tiktokPublishPreflight';
import { validateSizeChartForm } from '@/tiktok-platform/utils/tiktokSizeChart';
import { officialSizeChartDraftHasContent } from '@/tiktok-platform/utils/tiktokOfficialSizeChart';
import {
  collectEffectivelyRequiredAttrs,
  isEditorAttrFilled,
} from '@/tiktok-platform/utils/tiktokAttrVisibility';
import {
  TIKTOK_DETAIL_IMAGES_MAX,
  TIKTOK_MAIN_IMAGES_MAX,
  TIKTOK_MAIN_IMAGES_MIN,
} from '@/tiktok-platform/utils/tiktokProductImage';
import { validateTiktokCategoryCompliance } from '@/tiktok-platform/utils/tiktokCategoryRules';
import { normalizeTiktokIdentifierCode } from '@/tiktok-platform/utils/buildTiktokTtsCreateProductPayload';
import {
  parseTiktokStringList,
  resolveTiktokPreSaleRule,
  resolveTiktokScheduledSaleMillis,
  tiktokDaysFromNow,
  tiktokStringListChars,
  TIKTOK_DESCRIPTION_MAX_CHARS,
  TIKTOK_EXTERNAL_PRODUCT_ID_MAX,
  TIKTOK_INVENTORY_QTY_MIN,
  TIKTOK_INVENTORY_QTY_MAX,
  TIKTOK_KEY_FEATURES_MAX,
  TIKTOK_KEY_FEATURES_MAX_CHARS,
  TIKTOK_SCHEDULED_SALE_MAX_DAYS,
  TIKTOK_SEARCH_TERMS_MAX,
  TIKTOK_SEARCH_TERMS_MAX_CHARS,
  TIKTOK_US_LIST_PRICE_MAX,
  resolveTiktokTitleLengthBounds,
} from '@/tiktok-platform/utils/tiktokPublishFormLimits';
import { assertLatinCustomColorName } from '@/tiktok-platform/utils/tiktokColorNameMap';
import { firstFilledSkuPackageField } from '@/tiktok-platform/utils/tiktokPackageBatch';

export { resolveTiktokTitleLengthBounds } from '@/tiktok-platform/utils/tiktokPublishFormLimits';

/** package_dimensions 文档注明可选的市场 */
const TIKTOK_PACKAGE_DIMS_OPTIONAL_REGIONS = new Set(['ID', 'TH', 'VN']);
/** Create Product skus 上限 300 的市场（含 EU） */
const TIKTOK_SKU_CAP_300_REGIONS = new Set([
  'BR', 'MX', 'JP', 'GB', 'UK', 'US',
  'DE', 'ES', 'FR', 'IE', 'IT', 'NL', 'BE', 'AT', 'PT', 'PL', 'SE', 'DK', 'FI', 'GR',
]);

/** 发品提交：槽位是否有可预览的平台图（CDN 或 uri） */
function imageUrlOf(image) {
  if (!image) return '';
  return String(image.tiktokUrl || image.uri || image.tiktokUri || '').trim();
}

/** Create Product main_images 必须用 Upload Image 的 material uri（非 https） */
function imageMaterialUriOf(image) {
  if (!image || typeof image !== 'object') return '';
  const uri = String(image.uri || image.tiktokUri || '').trim();
  if (!uri || /^https?:\/\//i.test(uri) || uri.startsWith('//') || uri.startsWith('/')) return '';
  return uri;
}

function isPositiveNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0;
}

function isNonNegativeInteger(value) {
  const text = String(value ?? '').trim();
  if (!/^\d+$/.test(text)) return false;
  return Number(text) >= 0;
}

function isPositiveWholeNumber(value) {
  const text = String(value ?? '').trim();
  if (!/^\d+$/.test(text)) return false;
  return Number(text) > 0;
}

function fail(message, section, field = 'form') {
  return { ok: false, message: fixedUi(message), section, field };
}

function normalizeMarketRegion(raw) {
  const code = String(raw || '').trim().toUpperCase();
  return code === 'UK' ? 'GB' : code;
}

function resolveSkuCountCap(region) {
  const code = normalizeMarketRegion(region);
  if (!code) return 300;
  return TIKTOK_SKU_CAP_300_REGIONS.has(code) || code === 'GB' ? 300 : 100;
}

function collectProductMainImageUrls(form) {
  const urls = [];
  const push = (img) => {
    const u = imageUrlOf(img);
    if (u && !urls.includes(u)) urls.push(u);
  };
  push(form?.mainImage);
  (Array.isArray(form?.detailImages) ? form.detailImages : []).forEach(push);
  // 旧草稿回退：色块级主图 / 细节图
  if (!urls.length) {
    (Array.isArray(form?.skcList) ? form.skcList : []).forEach((skc) => {
      push(skc?.mainImage);
      (Array.isArray(skc?.detailImages) ? skc.detailImages : []).forEach(push);
    });
  }
  return urls;
}

function countSkus(skcList) {
  return (Array.isArray(skcList) ? skcList : []).reduce(
    (n, skc) => n + (Array.isArray(skc?.skuRows) ? skc.skuRows.length : 0),
    0,
  );
}

/**
 * Create Product size_chart：图片 或 平台模板 id 二选一。
 * 站内还可「本地填参 / 尺码库」——提交时会 resolve 成平台模板，故草稿有内容也算已满足。
 */
function hasSizeChartSatisfied(form) {
  if (imageUrlOf(form.sizeChartImage || form.tiktokSizeChartImage)) return true;
  const tplIds = Array.isArray(form.tiktokSizeTemplateIds) ? form.tiktokSizeTemplateIds : [];
  if (tplIds.some((id) => String(id || '').trim())) return true;
  const draft = form.officialSizeChart;
  if (officialSizeChartDraftHasContent(draft, draft?.groupList || [])) return true;
  return false;
}

/**
 * @param {object} form
 * @param {object} ctx
 * @param {boolean} [ctx.isEdit]
 * @param {string|number} [ctx.shopId]
 * @param {object|null} [ctx.attrTpl]
 * @param {object|null} [ctx.colorAttr]
 * @param {object|null} [ctx.sizeAttr]
 * @param {Array} [ctx.sizeMeasureAttrs]
 * @param {boolean} [ctx.sizeChartRequired] 官方 sizecharts.class.get / Category Rules
 * @param {Array} [ctx.warehouseOptions]
 * @param {string} [ctx.shopType]
 * @param {object} [ctx.categoryRules] Get Category Rules 归一化结果
 * @param {string} [ctx.region] 店铺市场（US/GB/…）
 * @param {number} [ctx.titleMaxLength] publish/standard 标题上限
 */
export function validateTiktokPublishForm(form = {}, ctx = {}) {
  const {
    isEdit = false,
    shopId,
    shopType = '',
    attrTpl = null,
    colorAttr = null,
    sizeAttr = null,
    sizeMeasureAttrs = [],
    sizeChartRequired = false,
    categoryRules = null,
    region = '',
    titleMaxLength = 0,
  } = ctx;

  const marketRegion = normalizeMarketRegion(region || form.region || form.tiktokCountry);

  if (!shopId) return fail('请先选择店铺', 'basic', 'form');

  const shopCapsEarly = tiktokShopCapabilities(shopType || form.shopType);
  if (!shopCapsEarly.mode) {
    return fail('当前店铺类型未知，无法按发品规则提交，请重新授权或同步店铺资料', 'basic', 'form');
  }

  const title = String(form.title || form.productName || '').trim();
  if (!title) return fail('请填写商品名称', 'basic', 'title');
  const titleBounds = resolveTiktokTitleLengthBounds(marketRegion, titleMaxLength);
  if (title.length < titleBounds.min) {
    return fail(fixedUiTpl('商品名称至少 {0} 个字符', titleBounds.min), 'basic', 'title');
  }
  if (title.length > titleBounds.max) {
    return fail(fixedUiTpl('商品名称不能超过 {0} 个字符', titleBounds.max), 'basic', 'title');
  }
  // 跨境运营：标题须英文（Create Product 无语言字段级禁中文；AI 标题亦只产出英文）
  if (/[\u3400-\u9fff]/.test(title)) {
    return fail('商品名称须为英文，不能包含中文', 'basic', 'title');
  }

  const description = String(form.description || '').trim();
  if (!description) {
    return fail('请填写商品描述（TikTok Create Product 必填，支持 HTML）', 'basic', 'description');
  }
  if (description.length > TIKTOK_DESCRIPTION_MAX_CHARS) {
    return fail(fixedUiTpl('商品描述不能超过 {0} 个字符', TIKTOK_DESCRIPTION_MAX_CHARS), 'basic', 'description');
  }
  const descriptionPlain = description.replace(/<[^>]+>/g, ' ');
  if (/[\u3400-\u9fff]/.test(descriptionPlain)) {
    return fail('商品描述须为英文，不能包含中文', 'basic', 'description');
  }
  const descImgCount = (description.match(/<img\b/gi) || []).length;
  if (descImgCount > 30) {
    return fail(fixedUiTpl('商品描述中的图片不能超过 30 张，当前 {0} 张', descImgCount), 'basic', 'description');
  }

  if (!form.categoryId) return fail('请选择商品类目（叶子类目 category_id）', 'basic', 'categoryId');

  // Create Product：external_product_id 选填，最长 999
  const productCode = String(form.supplierCode || form.externalProductId || '').trim();
  if (productCode && productCode.length > TIKTOK_EXTERNAL_PRODUCT_ID_MAX) {
    return fail(fixedUiTpl('货号（external_product_id）最长 {0} 位', TIKTOK_EXTERNAL_PRODUCT_ID_MAX), 'basic', 'supplierCode');
  }

  if (!String(form.currency || '').trim()) {
    return fail('缺少售价币种：请等待店铺币种加载完成', 'sales', 'currency');
  }

  const shopCaps = tiktokShopCapabilities(shopType || form.shopType);
  const skipSiteWarehouseFreight = Boolean(shopCaps.skipSiteWarehouseFreight);
  if (!skipSiteWarehouseFreight) {
    // shipping_template_id：Create Product 文档为 N，选填；有值才提交，不可用时可留空不传
    // inventory[].warehouse_id：Create Product 子字段必填（Y）
    if (!String(form.warehouseId || '').trim()) {
      return fail('请选择库存仓库（inventory.warehouse_id）', 'sales', 'warehouseId');
    }
  }

  const skcLabel = attributeDisplayLabel(colorAttr) || fixedUi("主规格");
  const skuSpecLabel = attributeDisplayLabel(sizeAttr) || fixedUi('其他规格');

  const skcList = Array.isArray(form.skcList) ? form.skcList : [];
  const coloredSkcs = skcList.filter((skc) => (
    skc?.colorValueId
    || String(skc?.colorExtraValue || '').trim()
    || String(skc?.colorLabel || '').trim()
  ));
  if (!coloredSkcs.length) {
    return fail(colorAttr ? fixedUiTpl('请选择或自定义{0}', skcLabel) : fixedUiTpl('请添加{0}', skcLabel), 'spec', 'skcList');
  }

  if (sizeAttr?.values?.length && !(form.selectedSizeIds || []).length) {
    return fail(fixedUiTpl('请选择{0}', skuSpecLabel), 'spec', 'skcList');
  }

  const skuCap = resolveSkuCountCap(marketRegion);
  const skuTotal = countSkus(coloredSkcs);
  if (skuTotal > skuCap) {
    return fail(fixedUiTpl('SKU 数量不能超过 {0}（当前 {1}）', skuCap, skuTotal), 'spec', 'supply');
  }

  // Create Product main_images：商品级主图+细节，至少 1、最多 9
  const mainImageUrls = collectProductMainImageUrls(form);
  if (mainImageUrls.length < TIKTOK_MAIN_IMAGES_MIN) {
    return fail(
      fixedUiTpl('请至少上传 {0} 张商品主图（须完成 TikTok 图片上传）', TIKTOK_MAIN_IMAGES_MIN),
      'spec',
      'mainImage',
    );
  }
  if (mainImageUrls.length > TIKTOK_MAIN_IMAGES_MAX) {
    return fail(
      fixedUiTpl('商品主图（main_images）最多 {0} 张，当前去重后 {1} 张，请删减后再提交', TIKTOK_MAIN_IMAGES_MAX, mainImageUrls.length),
      'spec',
      'mainImage',
    );
  }
  {
    const productMain = form.mainImage || coloredSkcs.find((s) => imageUrlOf(s?.mainImage))?.mainImage;
    if (!imageUrlOf(productMain)) {
      return fail('请上传商品主图（须完成 TikTok 平台转图）', 'spec', 'mainImage');
    }
    if (!imageMaterialUriOf(productMain)) {
      return fail(
        '商品主图缺少平台 uri（不能只用 CDN 链接）。请重新本地上传或从图片空间选用，完成平台转图后再发品',
        'spec',
        'mainImage',
      );
    }
    const details = Array.isArray(form.detailImages) && form.detailImages.length
      ? form.detailImages
      : (coloredSkcs.find((s) => (s?.detailImages || []).length)?.detailImages || []);
    if (details.length > TIKTOK_DETAIL_IMAGES_MAX) {
      return fail(
        fixedUiTpl('细节图最多 {0} 张（主图+细节合计不超过 {1} 张）', TIKTOK_DETAIL_IMAGES_MAX, TIKTOK_MAIN_IMAGES_MAX),
        'spec',
        'mainImage',
      );
    }
    for (let i = 0; i < details.length; i += 1) {
      if (!imageUrlOf(details[i])) {
        return fail(fixedUiTpl('第 {0} 张细节图无效，请重新上传', i + 1), 'spec', 'mainImage');
      }
      if (!imageMaterialUriOf(details[i])) {
        return fail(
          fixedUiTpl('第 {0} 张细节图缺少平台 uri，请重新选用并完成平台转图', i + 1),
          'spec',
          'mainImage',
        );
      }
    }
  }

  const packageDimsRequired = categoryRules?.packageDimensionRequired === true
    || !TIKTOK_PACKAGE_DIMS_OPTIONAL_REGIONS.has(marketRegion);
  const isUsMarket = marketRegion === 'US';
  const identifierCodesSeen = new Set();

  // Create Product：package_weight / package_dimensions 为商品级，一商品填一次
  {
    const weight = String(form.weightGrams ?? '').trim()
      || firstFilledSkuPackageField(form.skcList, 'weight');
    if (!weight) {
      return fail('请填写包裹重量（单位 g）', 'size', 'weightGrams');
    }
    if (!isPositiveNumber(weight)) {
      return fail('包裹重量须为大于 0 的数字（单位 g）', 'size', 'weightGrams');
    }
    const length = String(form.pkgLength ?? '').trim()
      || firstFilledSkuPackageField(form.skcList, 'length');
    const width = String(form.pkgWidth ?? '').trim()
      || firstFilledSkuPackageField(form.skcList, 'width');
    const height = String(form.pkgHeight ?? '').trim()
      || firstFilledSkuPackageField(form.skcList, 'height');
    const dimsFilled = Boolean(length && width && height);
    if (packageDimsRequired && !dimsFilled) {
      return fail('请填写包裹尺寸（长/宽/高，单位 cm，正整数）', 'size', 'pkgLength');
    }
    if (dimsFilled) {
      if (!isPositiveWholeNumber(length) || !isPositiveWholeNumber(width) || !isPositiveWholeNumber(height)) {
        return fail('包裹尺寸须为正整数（单位 cm）', 'size', 'pkgLength');
      }
    }
  }

  for (const skc of coloredSkcs) {
    const resolved = resolveSkcColorAgainstTemplate(skc, colorAttr);
    const colorName = resolved.colorLabel || resolved.colorExtraValue || skc.colorLabel || skc.colorExtraValue || skcLabel;
    const colorAttrId = String(resolved.colorAttributeId || colorAttr?.attributeId || '').trim();
    if (colorAttr && !colorAttrId) {
      return fail(fixedUiTpl('「{0}」缺少{1}属性，请刷新类目属性模板后重试', colorName, skcLabel), 'spec', 'skcList');
    }
    if (colorAttr && isBlankTiktokAttributeValueId(resolved.colorValueId)) {
      const customLabel = String(resolved.colorExtraValue || resolved.colorLabel || '').trim();
      if (!customLabel) {
        return fail(
          fixedUiTpl('「{0}」的{1}未匹配到平台属性值，请在模板中重新选择颜色', colorName, skcLabel),
          'spec',
          'skcList',
        );
      }
      const colorCheck = assertLatinCustomColorName(customLabel);
      if (!colorCheck.ok) {
        return fail(
          fixedUiTpl('「{0}」{1}', customLabel, fixedUi(colorCheck.message)),
          'spec',
          'skcList',
        );
      }
    }
    // 变体图（sku_img）：按颜色维护；旧草稿回退色块主图
    const variantImg = skc.skuImage || skc.mainImage;
    if (!imageUrlOf(variantImg)) {
      return fail(fixedUiTpl('请为「{0}」上传变体图（须完成 TikTok 平台转图）', colorName), 'spec', 'supply');
    }
    if (!imageMaterialUriOf(variantImg)) {
      return fail(
        fixedUiTpl('「{0}」变体图缺少平台 uri（不能只用 CDN 链接）。请重新本地上传或从图片空间选用，完成平台转图后再发品', colorName),
        'spec',
        'supply',
      );
    }

    const skuRows = Array.isArray(skc.skuRows) ? skc.skuRows : [];
    if (!skuRows.length) {
      return fail(fixedUiTpl('「{0}」缺少 SKU 供应信息，请选择{1}或检查规格', colorName, skuSpecLabel), 'spec', 'supply');
    }

    for (const sku of skuRows) {
      const sizeLabel = sku.sizeValueId || sku.sizeLabel || 'SKU';
      const price = sku.price ?? sku.supplyPrice ?? form.supplyPrice;
      if (price === '' || price == null) {
        return fail(fixedUiTpl('请填写「{0} / {1}」售价', colorName, sizeLabel), 'spec', 'supply');
      }
      if (!isPositiveNumber(price)) {
        return fail(fixedUiTpl('「{0} / {1}」售价须为大于 0 的数字', colorName, sizeLabel), 'spec', 'supply');
      }

      const stock = sku.stock === '' || sku.stock == null ? form.stockQty : sku.stock;
      if (stock === '' || stock == null) {
        return fail(fixedUiTpl('请填写「{0} / {1}」库存', colorName, sizeLabel), 'spec', 'supply');
      }
      if (!isNonNegativeInteger(stock)) {
        return fail(fixedUiTpl('「{0} / {1}」库存须为整数', colorName, sizeLabel), 'spec', 'supply');
      }
      const stockNum = Number(stock);
      // Create Product：inventory.quantity Valid range [1, 99999]；传 0 会 11001002
      if (stockNum < TIKTOK_INVENTORY_QTY_MIN) {
        return fail(
          fixedUiTpl('「{0} / {1}」库存至少为 {2}（平台 inventory.quantity 不允许 0）', colorName, sizeLabel, TIKTOK_INVENTORY_QTY_MIN),
          'spec',
          'supply',
        );
      }
      if (stockNum > TIKTOK_INVENTORY_QTY_MAX) {
        return fail(
          fixedUiTpl('「{0} / {1}」库存不能超过 {2}', colorName, sizeLabel, TIKTOK_INVENTORY_QTY_MAX),
          'spec',
          'supply',
        );
      }

      // US list_price（划线价）：填了则须 ≥ 售价且 ≤ 7600
      if (isUsMarket) {
        const listRaw = String(sku.suggestedPrice ?? form.suggestedPrice ?? '').trim();
        if (listRaw) {
          const listNum = Number(listRaw);
          const priceNum = Number(price);
          if (!Number.isFinite(listNum) || listNum <= 0) {
            return fail(fixedUiTpl('「{0} / {1}」划线价须为大于 0 的数字', colorName, sizeLabel), 'spec', 'supply');
          }
          if (listNum < priceNum) {
            return fail(
              fixedUiTpl('「{0} / {1}」划线价（list_price）不能低于售价', colorName, sizeLabel),
              'spec',
              'supply',
            );
          }
          if (listNum > TIKTOK_US_LIST_PRICE_MAX) {
            return fail(
              fixedUiTpl('「{0} / {1}」划线价不能超过 {2}', colorName, sizeLabel, TIKTOK_US_LIST_PRICE_MAX),
              'spec',
              'supply',
            );
          }
        }
      }

      // identifier_code：填了则格式合法且 SKU 间唯一（勿静默丢弃）
      const rawCode = String(sku.identifierCode ?? sku.identifier_code ?? '').trim();
      if (rawCode) {
        const normalized = normalizeTiktokIdentifierCode(
          rawCode,
          sku.identifierType ?? sku.identifier_code_type,
        );
        if (!normalized) {
          return fail(
            fixedUiTpl('「{0} / {1}」商品条码格式无效（支持 GTIN/EAN/UPC/ISBN/JAN）', colorName, sizeLabel),
            'spec',
            'supply',
          );
        }
        const codeKey = `${normalized.type}:${normalized.code}`;
        if (identifierCodesSeen.has(codeKey)) {
          return fail(
            fixedUiTpl('商品条码「{0}」重复，每个 SKU 的 identifier_code 须唯一', normalized.code),
            'spec',
            'supply',
          );
        }
        identifierCodesSeen.add(codeKey);
      }

      if (sizeAttr?.values?.length && !sku.sizeValueId) {
        return fail(fixedUiTpl('「{0}」SKU 缺少{1}，请重新勾选{2}', colorName, skuSpecLabel, skuSpecLabel), 'spec', 'skcList');
      }
    }

  }

  // video：Create Product 仅接受 Upload Product File 返回的 id
  {
    const videos = Array.isArray(form.tiktokVideos) ? form.tiktokVideos : [];
    for (const v of videos) {
      const vid = String(v?.vid || v?.id || '').trim();
      if (!vid) {
        return fail('主图视频未完成 TikTok 上传（需要 video.id），请重新上传', 'spec', 'tiktokVideos');
      }
    }
  }

  // —— Create Product 补充字段：预约上架 / 搜索词 / 卖点 / 限购 / 预售 ——
  // 预约上架：编辑走部分编辑（不带该字段），且排期过期后不应再卡住编辑，故仅创建时校验
  const scheduleRaw = isEdit ? '' : String(form.hopeOnSaleDate ?? '').trim();
  // 上架方式 = 预约上架时必须给时间（自动上架不提交 scheduled_sale）
  if (!isEdit && String(form.shelfWay || '1') === '2' && !scheduleRaw) {
    return fail('已选择预约上架，请填写上架时间', 'sales', 'hopeOnSaleDate');
  }
  if (scheduleRaw) {
    const scheduleMs = resolveTiktokScheduledSaleMillis(scheduleRaw);
    if (!(scheduleMs > 0)) {
      return fail('预约上架时间无效，请重新选择日期时间', 'sales', 'hopeOnSaleDate');
    }
    const scheduleDays = (scheduleMs - Date.now()) / 86400000;
    if (scheduleDays <= 0) {
      return fail('预约上架时间须晚于当前时间', 'sales', 'hopeOnSaleDate');
    }
    if (scheduleDays > TIKTOK_SCHEDULED_SALE_MAX_DAYS) {
      return fail(
        fixedUiTpl('预约上架时间不能超过 {0} 天', TIKTOK_SCHEDULED_SALE_MAX_DAYS),
        'sales',
        'hopeOnSaleDate',
      );
    }
  }

  // search_terms：≤15 条、合计 ≤250 字符（Create Product）且不对买家展示
  const searchTerms = parseTiktokStringList(form.searchTerms);
  if (searchTerms.length > TIKTOK_SEARCH_TERMS_MAX) {
    return fail(
      fixedUiTpl('搜索词最多 {0} 个（当前 {1}）', TIKTOK_SEARCH_TERMS_MAX, searchTerms.length),
      'basic',
      'searchTerms',
    );
  }
  const searchTermChars = tiktokStringListChars(searchTerms);
  if (searchTermChars > TIKTOK_SEARCH_TERMS_MAX_CHARS) {
    return fail(
      fixedUiTpl('搜索词合计不能超过 {0} 个字符（当前 {1}）', TIKTOK_SEARCH_TERMS_MAX_CHARS, searchTermChars),
      'basic',
      'searchTerms',
    );
  }

  // key_product_features：≤5 条、合计 ≤1500 字符
  const keyFeatures = parseTiktokStringList(form.keyProductFeatures);
  if (keyFeatures.length > TIKTOK_KEY_FEATURES_MAX) {
    return fail(
      fixedUiTpl('商品卖点最多 {0} 条（当前 {1}）', TIKTOK_KEY_FEATURES_MAX, keyFeatures.length),
      'basic',
      'keyProductFeatures',
    );
  }
  const keyFeatureChars = tiktokStringListChars(keyFeatures);
  if (keyFeatureChars > TIKTOK_KEY_FEATURES_MAX_CHARS) {
    return fail(
      fixedUiTpl('商品卖点合计不能超过 {0} 个字符（当前 {1}）', TIKTOK_KEY_FEATURES_MAX_CHARS, keyFeatureChars),
      'basic',
      'keyProductFeatures',
    );
  }

  // 限购：正整数；两者皆填时累计须 ≥ 单次（Create Product purchase_order_quantity_limit）
  const maxOrderQtyRaw = String(form.maxOrderQuantity ?? '').trim();
  const cumulativeQtyRaw = String(form.cumulativeOrderQuantity ?? '').trim();
  if (maxOrderQtyRaw && !isPositiveWholeNumber(maxOrderQtyRaw)) {
    return fail('单次限购须为大于 0 的整数', 'sales', 'maxOrderQuantity');
  }
  if (cumulativeQtyRaw && !isPositiveWholeNumber(cumulativeQtyRaw)) {
    return fail('累计限购须为大于 0 的整数', 'sales', 'cumulativeOrderQuantity');
  }
  if (maxOrderQtyRaw && cumulativeQtyRaw
    && Number(cumulativeQtyRaw) < Number(maxOrderQtyRaw)) {
    return fail('累计限购数量不能小于单次限购数量', 'sales', 'cumulativeOrderQuantity');
  }

  // 预售（skus[].pre_sale）：仅类目规则允许时可用；天数/日期须落在文档区间
  const preSaleType = String(form.preSaleType ?? '').trim().toUpperCase();
  if (preSaleType) {
    const allowedPreSaleTypes = Array.isArray(categoryRules?.allowedSpecialProductTypes)
      ? categoryRules.allowedSpecialProductTypes
      : [];
    // 规则未加载时放行（与 validateTiktokCategoryCompliance 同口径），由平台裁定
    if (categoryRules?.loaded && !allowedPreSaleTypes.includes(preSaleType)) {
      return fail(
        fixedUiTpl('当前类目不支持预售类型「{0}」，请改选其它类型或留空', preSaleType),
        'sales',
        'preSaleType',
      );
    }
    const preSaleRule = resolveTiktokPreSaleRule(marketRegion, preSaleType);
    if (!preSaleRule) {
      return fail(
        fixedUiTpl('当前市场（{0}）不支持预售类型「{1}」', marketRegion || fixedUi('未知'), preSaleType),
        'sales',
        'preSaleType',
      );
    }
    if (preSaleRule.fulfillment === 'release_date') {
      const releaseDays = tiktokDaysFromNow(form.preSaleReleaseDate);
      if (!Number.isFinite(releaseDays)) {
        return fail('请填写预售上架时间', 'sales', 'preSaleReleaseDate');
      }
      if (releaseDays < preSaleRule.minDays || releaseDays > preSaleRule.maxDays) {
        return fail(
          fixedUiTpl('预售上架时间须在当前起 {0}–{1} 天内', preSaleRule.minDays, preSaleRule.maxDays),
          'sales',
          'preSaleReleaseDate',
        );
      }
    } else {
      const handlingRaw = String(form.preSaleHandlingDays ?? '').trim();
      if (!isPositiveWholeNumber(handlingRaw)) {
        return fail('请填写预售发货时长（大于 0 的整数天）', 'sales', 'preSaleHandlingDays');
      }
      const handlingDays = Number(handlingRaw);
      if (handlingDays < preSaleRule.minDays || handlingDays > preSaleRule.maxDays) {
        return fail(
          fixedUiTpl(
            '预售发货时长须为 {0}–{1} 天{2}',
            preSaleRule.minDays,
            preSaleRule.maxDays,
            fixedUi(preSaleRule.unit === 'BUSINESS_DAY' ? '（工作日）' : '（日历日）'),
          ),
          'sales',
          'preSaleHandlingDays',
        );
      }
    }
  }

  const skuUniqueLocal = validateSupplierSkusLocal(form);
  if (!skuUniqueLocal.ok) return skuUniqueLocal;

  const productAttrsPool = Array.isArray(attrTpl?.productAttrs) && attrTpl.productAttrs.length
    ? attrTpl.productAttrs
    : [
      ...(attrTpl?.requiredProduct || []),
      ...(attrTpl?.optionalProduct || []),
      ...(attrTpl?.mainAttrs || []),
      ...(attrTpl?.otherAttrs || []),
    ];
  const { singles: requiredSingles, groups: requiredGroups } = collectEffectivelyRequiredAttrs(
    productAttrsPool,
    form.productAttributes || {},
  );
  for (const group of requiredGroups) {
    return fail(fixedUiTpl('请至少填写一项：{0}', group.label), 'attrs', 'productAttributes');
  }
  for (const attr of requiredSingles) {
    const val = form.productAttributes?.[attr.attributeId];
    if (attr.mode === 4 || attr.isComposition) {
      const rows = Array.isArray(val) ? val : [];
      if (!rows.length) return fail(fixedUiTpl('请填写关键属性：{0}', attr.name), 'attrs', 'productAttributes');
      for (const row of rows) {
        if (!row?.valueId) return fail(fixedUiTpl('{0}：请选择成分项', attr.name), 'attrs', 'productAttributes');
        if (!isPositiveNumber(row.percent)) return fail(fixedUiTpl('{0}：比例须为大于 0 的数字', attr.name), 'attrs', 'productAttributes');
      }
      const total = rows.reduce((s, r) => s + (Number(r.percent) || 0), 0);
      if (Math.round(total) !== 100) return fail(fixedUiTpl('{0} 比例合计须为 100%', attr.name), 'attrs', 'productAttributes');
    } else if (attr.mode === 1) {
      if (!Array.isArray(val) || !val.length) return fail(fixedUiTpl('请选择关键属性：{0}', attr.name), 'attrs', 'productAttributes');
    } else if (!isEditorAttrFilled(attr, form.productAttributes || {})) {
      return fail(fixedUiTpl('请填写关键属性：{0}', attr.name), 'attrs', 'productAttributes');
    }
  }

  // 类目规则：认证 / 制造商 / 欧代 / PFAND
  const complianceError = validateTiktokCategoryCompliance(form, categoryRules);
  if (complianceError) {
    return fail(complianceError.message, complianceError.section, complianceError.field);
  }

  // 尺码表（Create Product size_chart）：类目必填时，图片 / 平台模板 / 本地填参（含尺码库）任选其一。
  // 细项（必填测量列等）由官方尺码表面板 validate + resolveForSubmit 负责，此处只挡「完全没填」。
  if (sizeChartRequired && !hasSizeChartSatisfied(form)) {
    return fail(
      '当前类目要求尺码表：请填写下方尺寸参数、从尺码库选用，或上传尺码表图片（任选其一即可）',
      'spec',
      'sizeChart',
    );
  }
  // 非官方尺码表类目时，才走旧测量属性矩阵校验；官方面板类目不要重复拦
  if (!sizeChartRequired && sizeMeasureAttrs.length) {
    const sizeCheck = validateSizeChartForm({
      sizeChart: form.sizeChart,
      measureAttrs: sizeMeasureAttrs,
      sizeAttr,
      selectedSizeIds: form.selectedSizeIds || [],
      skcList: form.skcList || [],
    });
    if (!sizeCheck.ok) {
      return fail(sizeCheck.message, sizeCheck.section || 'size', sizeCheck.field || 'sizeChart');
    }
  }

  return { ok: true };
}
